import React from 'react';
import { Row } from 'reactstrap';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import EmailSubscribeBox from 'Components/EmailSubscribe/EmailSubscribeBox';
import AssistantBanner from 'Components/Assistant/AssistantBanner';
import Meta from 'Components/Utility/SEO/Meta';
import ProductRecommendations from 'Containers/Product/ProductRecommendations';
import SocialPages from 'Components/Utility/SocialPages/SocialPages';
import SplashHeader from 'Components/Banner/SplashHeader';
import VoucherBox from 'Components/Vouchers/VoucherBox';
import VoucherDeliveryBox from 'Components/Vouchers/VoucherDeliveryBox';
import VoucherFaqs from 'Components/Vouchers/VoucherFaqs';

import 'Components/Vouchers/Vouchers.scss';

const messages = defineMessages({
  title: {
    id: 'vouchers.title',
    defaultMessage: 'Official Fanatical Vouchers, Discount Codes, Coupons and Offers | Fanatical',
  },
  meta_description: {
    id: 'vouchers.meta_description',
    defaultMessage: 'Official source for the latest Vouchers, Discount Codes, Coupons and Offers from Fanatical. Get massive savings Steam games for PC, Mac, and Linux.',
  },
  topsellers: {
    id: 'vouchers.topsellers',
    defaultMessage: 'Top Sellers',
  },
  splash_header_title: {
    id: 'vouchers.spash_header.title',
    defaultMessage: 'Fanatical Coupons',
  },
});

const Vouchers = () => {
  const { formatMessage } = useIntl();
  const vouchers = useSelector(state => state.vouchers.all);
  const appliedVoucher = useSelector(state => state.voucher);
  const deliveries = useSelector(state => state.deliveries.all);
  const sortedVouchers = sortBy(vouchers, ['sort_order']);

  return (
    <main className="vouchers-page pb-5">
      <Meta
        title={formatMessage(messages.title)}
        url="coupons-vouchers-codes"
        description={formatMessage(messages.meta_description)}
      />
      <SplashHeader
        title={formatMessage(messages.splash_header_title)}
      />

      <div className="pt-5 pb-4 container">
        <Row>
          <section className="col-12 col-lg-8">
            <h1>
              <FormattedMessage id="vouchers.header-second" defaultMessage="Get the latest Fanatical coupons" />
            </h1>
            {isEmpty(vouchers) && isEmpty(deliveries) &&
            <div className="no-vouchers">
              <FormattedMessage id="vouchers.no-vouchers" defaultMessage="Sorry - There are currently no available coupons." />
            </div>
              }
            {!!(!isEmpty(sortedVouchers) || !isEmpty(deliveries)) &&
            <div className="voucher-box-container">
              {!isEmpty(sortedVouchers) && sortedVouchers.map(voucher =>
                <VoucherBox
                  voucher={voucher}
                  key={voucher._id}
                  appliedVoucher={appliedVoucher}
                />,
              )}
              {!isEmpty(deliveries) && deliveries.map(delivery =>
                <VoucherDeliveryBox
                  delivery={delivery}
                  key={delivery._id}
                />,
              )}
            </div>
              }

            <section>
              <h2>
                <FormattedMessage id="vouchers.coupon-codes" defaultMessage="How to use our coupon/voucher codes" />
              </h2>
              <div className="how-to-container">
                <div className="how-to-number">1</div>
                <div className="text-container">
                  <div className="how-to-title">
                    <span><FormattedMessage id="vouchers.numeric-1" defaultMessage="1." tagName="span" /></span>
                    <FormattedMessage id="vouchers.numeric-1-info" defaultMessage="Choose your coupon - find the full list of active codes above" tagName="span" />
                  </div>
                  <div className="how-to-copy">
                    <p>
                      <FormattedMessage
                        id="vouchers.numeric-1-info-2"
                        defaultMessage="Looking to save even more cash? We're here to help! Check our list above for a full list of active coupon codes (or voucher codes) that you can use right now to grab even bigger discounts on the hottest games. Want to be informed when we launch a new code? Make sure you sign up to our email newsletter and install the {link} for access to exclusive coupon codes."
                        values={{
                          link: <Link to={'/browser-extension'}><FormattedMessage id="vouchers.numeric-1-link" defaultMessage="Fanatical Browser Extension" /></Link>,
                        }}
                      />
                    </p>
                  </div>
                </div>
              </div>
              <div className="how-to-container">
                <div className="how-to-number">2</div>
                <div className="text-container">
                  <div className="how-to-title">
                    <span><FormattedMessage id="vouchers.numeric-2" defaultMessage="2." /></span>
                    <FormattedMessage id="vouchers.numeric-2-header" defaultMessage="Copy code - or use the Fanatical PC Game Price Comparison extension for one-click savings" tagName="span" />
                  </div>
                  <div className="how-to-copy">
                    <p>
                      <FormattedMessage
                        id="vouchers.numeric-2-info"
                        defaultMessage="Chosen your code? Great! Make sure you copy it to add to your cart when ready. Got {link}? Then it's even easier, simply apply it automatically using your browser extension."
                        values={{
                          link: <Link to={'/browser-extension'}><FormattedMessage id="vouchers.numeric-2-link" defaultMessage="Fanatical Browser Extension" /></Link>,
                        }}
                      />
                    </p>
                  </div>
                </div>
              </div>
              <div className="how-to-container">
                <div className="how-to-number">3</div>
                <div className="text-container">
                  <div className="how-to-title">
                    <span><FormattedMessage id="vouchers.numeric-3" defaultMessage="3." tagName="span" /></span>
                    <FormattedMessage id="vouchers.numeric-3-header" defaultMessage="Choose your games" tagName="span" />
                  </div>
                  <div className="how-to-copy">
                    <p>
                      <FormattedMessage id="vouchers.numeric-3-info" defaultMessage="Our store is packed with awesome deals, so check out what's on offer and add your favorites to your cart. Don't forget to check that they are eligible with the coupon code you've chosen." />
                    </p>
                  </div>
                </div>
              </div>
              <div className="how-to-container">
                <div className="how-to-number">4</div>
                <div className="text-container">
                  <div className="how-to-title">
                    <span>
                      <FormattedMessage id="vouchers.numeric-4" defaultMessage="4." tagName="span" />
                    </span>
                    <FormattedMessage id="vouchers.numeric-4-header" defaultMessage="Checkout and show off your savings" tagName="span" />
                  </div>
                  <div className="how-to-copy">
                    <p>
                      <FormattedMessage id="vouchers.numeric-4-info" defaultMessage="Picked all your games? Now just head over to the cart and apply your code using the dropdown in the Cart Summary. Of course, if you've already applied it through the Fanatical PC Game Price Comparison then you're one step ahead. Complete your purchase and don't forget to tell your mates how much you've saved." />
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <div className="voucher-faqs-container">
                <h2>
                  <FormattedMessage id="vouchers.voucher-faq" defaultMessage="Coupon/Voucher FAQs" />
                </h2>
                <VoucherFaqs />
              </div>
            </section>
          </section>

          <aside className="col-12 col-lg-4">
            <AssistantBanner lightTheme />
            <EmailSubscribeBox offWhiteTheme context="Voucher page" />
            <SocialPages lightTheme />
            <div className="coupons-top-sellers">
              <ProductRecommendations
                forceTopSellers
                lightTheme
                title={formatMessage(messages.topsellers)}
                xsSize={12}
                smSize={6}
                lgSize={12}
                slidesToShow={1}
              />
            </div>
          </aside>
        </Row>
      </div>
    </main>
  );
};

export default Vouchers;
